import React, { useContext } from "react";

import { ListItemIcon, ListItemText, AppBar, Box, Toolbar,
    IconButton, Menu, Container, Button, MenuItem,
    Avatar, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TableRestaurantOutlinedIcon from '@mui/icons-material/TableRestaurantOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';

import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';

import {
    usePopupState,
    bindTrigger,
    bindMenu,
    } from 'material-ui-popup-state/hooks'

import { stringAvatar } from "../utils/colorutils";
import { AuthContext } from "../contexts/auth-context";
import { GlobalContext } from "../contexts/global-context";
import { Logo } from "../assets/images/logo";
import useApi from "../services/api";

export function MainNavBar({ transparent = false, hideLogo = false, lightContent = false }) {
    const { logoutUser } = useApi();
    const { setShowAuthModal, setAuthModalType, isMobile } = useContext(GlobalContext);
    const { user, userDetails, avatarVersion } = useContext(AuthContext);
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (to) => {
        const path = location.pathname;
        if (to === '' || to === '/') return path === '/';
        return path === `/${to}` || path.startsWith(`/${to}/`);
    };

    const guestMenu = usePopupState({ variant: 'popover', popupId: 'guestMenu' })
    const userMenu = usePopupState({ variant: 'popover', popupId: 'userMenu' })

    const handleMenuClick = (e) => {
        if (e.target.innerText === "Logout") {
            logoutUser();
        }
    }

    const handleProfileClick = () => {
        navigate('/settings');
    }

    let avatarProps = {};

    if (user) {
      if (userDetails?.profile_img) {
        avatarProps = {
          src: `${userDetails.profile_img}?v=${avatarVersion}`,
          alt: user.username
        };
      } else {
        avatarProps = stringAvatar(user.username, user.email);
      }
    }

    const appBar = (
        <AppBar
        color={transparent ? "transparent" : "background"}
        elevation={0}
        position={transparent ? "absolute" : "static"}
        sx={{
            backgroundColor: transparent
                ? "transparent"
                : theme.palette.background.paper,
            ...(!transparent && {
                borderBottom: '1px solid',
                borderColor: 'divider',
            }),
            ...(transparent && {
                zIndex: theme.zIndex.appBar,
            })
        }}
    >
        <Container maxWidth="lg" disableGutters={isMobile}>
            <Toolbar sx={{ px: { xs: 0 } }}>
                {hideLogo ? null : (
                    <a href="/" style={{height: '100%'}}>
                        <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                            <Logo
                                width={isMobile ? 140 : 170 }
                                primaryFill={lightContent ? theme.palette.primary.main : theme.palette.common.black}
                                secondaryFill={lightContent ? theme.palette.common.white : theme.palette.primary.main}
                            />
                        </Box>
                    </a>
                )}

                {/* Spacer */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Desktop Nav Links - right-aligned */}
                {!isMobile && (
                    <Box sx={{
                        display: 'flex',
                        alignSelf: 'flex-end',
                        alignItems: 'flex-end',
                        gap: 2,
                        pb: '14px'
                    }}>
                        {[
                            { to: 'designs', label: 'Designs' },
                            { to: 'faq', label: 'FAQ' },
                            { to: 'contact', label: 'Contact' },
                        ].map(({ to, label }) => (
                            <Button
                                key={to}
                                component={RouterLink}
                                to={to}
                                sx={{
                                    color: isActive(to)
                                        ? (lightContent ? theme.palette.primary.light : theme.palette.primary.main)
                                        : (lightContent ? theme.palette.common.white : theme.palette.text.primary),
                                    textDecoration: 'none',
                                    fontSize: '1rem',
                                    fontWeight: isActive(to) ? 600 : 500,
                                    borderRadius: 0,
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                    minHeight: 0,
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: isActive(to) ? 'calc(100% - 16px)' : 0,
                                        height: '2px',
                                        backgroundColor: lightContent ? theme.palette.common.white : theme.palette.common.black,
                                        transition: 'width 0.2s ease',
                                    },
                                    '&:hover::after': {
                                        width: 'calc(100% - 16px)',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        color: lightContent ? theme.palette.primary.light : theme.palette.primary.main,
                                    }
                                }}
                            >
                                {label}
                            </Button>
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0 : 2, ml: { md: 4 } }}>

                
                {!user && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: isMobile ? 1 : 2,
                    }}>
                        <Button 
                            variant="contained"
                            size={isMobile ? "small" : "large"}
                            sx={{ 
                                minWidth: isMobile ? 'auto' : '80px',
                                px: isMobile ? 1 : 2,
                                py: isMobile ? 0.5 : 0.75,
                                ...(lightContent && {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.common.white,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    }
                                })
                            }}
                            onClick={() => {
                                setAuthModalType("login");
                                setShowAuthModal(true);
                            }}
                        >
                        Log in
                        </Button>

                        <Button 
                            variant="outlined"
                            size={isMobile ? "small" : "large"}
                            sx={{
                                minWidth: isMobile ? 'auto' : '80px',
                                px: isMobile ? 1 : 2,
                                py: isMobile ? 0.5 : 0.75,
                                ...(lightContent && {
                                    borderColor: theme.palette.common.white,
                                    color: theme.palette.common.white,
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.common.white,
                                    }
                                })
                            }}
                            onClick={() => {
                                setAuthModalType("register");
                                setShowAuthModal(true);
                            }}
                            >
                            Sign up
                        </Button>

                        {isMobile && (
                            <IconButton
                            {...bindTrigger(guestMenu)}
                            size={isMobile ? "small" : "large"}
                            aria-label="open theme menu"
                            sx={{
                                ...(lightContent && {
                                    color: theme.palette.common.white,
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                    }
                                })
                            }}
                            >
                            <MenuIcon sx={{ fontSize: 30 }} />
                            </IconButton>
                        )}

                        <Menu
                        {...bindMenu(guestMenu)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        onClose={guestMenu.close}
                        >
                            {isMobile && [
                                { key: 'designs', to: 'designs', icon: <TableRestaurantOutlinedIcon fontSize="small" sx={{ color: 'text.primary' }} />, label: 'Designs' },
                                { key: 'faq', to: 'faq', icon: <HelpOutlineIcon fontSize="small" sx={{ color: 'text.primary' }} />, label: 'FAQ' },
                                { key: 'contact', to: 'contact', icon: <MessageOutlinedIcon fontSize="small" sx={{ color: 'text.primary' }} />, label: 'Contact Us' },
                            ].map(({ key, to, icon, label }) => (
                                <MenuItem
                                    key={key}
                                    onClick={guestMenu.close}
                                    component={RouterLink}
                                    to={to}
                                    sx={isActive(to) ? {
                                        borderLeft: '3px solid',
                                        borderLeftColor: 'primary.main',
                                        backgroundColor: 'action.selected',
                                    } : {}}
                                >
                                    <ListItemIcon>{icon}</ListItemIcon>
                                    <ListItemText>{label}</ListItemText>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                )}
                    {user && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                            {/* Profile Avatar - Clickable to go to settings */}
                            <IconButton
                                sx={{ p: 0 }}
                                onClick={handleProfileClick}
                                aria-label="Go to profile settings"
                            >
                                <Avatar {...avatarProps} />
                            </IconButton>

                            {/* Hamburger Menu */}
                            <IconButton
                                {...bindTrigger(userMenu)}
                                size={isMobile ? "small" : "large"}
                                aria-label="open user menu"
                                data-hint="user-menu-button"
                                sx={{
                                    ...(lightContent && {
                                        color: theme.palette.common.white,
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                        }
                                    })
                                }}
                            >
                                <MenuIcon sx={{ fontSize: isMobile ? 30 : undefined }} />
                            </IconButton>

                            <Menu
                                {...bindMenu(userMenu)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                onClick={handleMenuClick}
                                onClose={userMenu.close}
                            >
                                {isMobile && [
                                    ...[
                                        { key: 'designs', to: 'designs', icon: <TableRestaurantOutlinedIcon fontSize="small" sx={{ color: 'text.primary' }} />, label: 'Designs' },
                                        { key: 'faq', to: 'faq', icon: <HelpOutlineIcon fontSize="small" sx={{ color: 'text.primary' }} />, label: 'FAQ' },
                                        { key: 'contact', to: 'contact', icon: <MessageOutlinedIcon fontSize="small" sx={{ color: 'text.primary' }} />, label: 'Contact Us' },
                                    ].map(({ key, to, icon, label }) => (
                                        <MenuItem
                                            key={key}
                                            onClick={userMenu.close}
                                            component={RouterLink}
                                            to={to}
                                            sx={isActive(to) ? {
                                                borderLeft: '3px solid',
                                                borderLeftColor: 'primary.main',
                                                backgroundColor: 'action.selected',
                                            } : {}}
                                        >
                                            <ListItemIcon>{icon}</ListItemIcon>
                                            <ListItemText>{label}</ListItemText>
                                        </MenuItem>
                                    )),
                                    <Divider key="divider" />
                                ]}
                                <MenuItem
                                    onClick={userMenu.close}
                                    component={RouterLink}
                                    to="library"
                                    data-hint="my-models-menu"
                                >
                                    <ListItemIcon>
                                        <CollectionsBookmarkIcon fontSize="small"
                                            sx={{ color: 'text.primary' }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText>My Library</ListItemText>
                                </MenuItem>
                                <MenuItem onClick={userMenu.close} >
                                    <ListItemIcon>
                                        <LogoutIcon fontSize="small" 
                                            sx={{ color: 'text.primary' }} 
                                        />
                                    </ListItemIcon>
                                    <ListItemText>Logout</ListItemText>
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}
                </Box>
            </Toolbar>
        </Container>
    </AppBar>
    ) 

    return appBar;
}